import { useMutation, useQuery } from "react-query";
import {
  deleteVoucherById,
  getVouchers,
  uploadVouchers,
  downloadVoucherById,
  getVoucherHistory,
} from "../services/vouchers";
import { queryClient } from "../App";
import { SetterOrUpdater, useSetRecoilState } from "recoil";
import { snackbarAtom } from "../selectorsAtoms";
import { uniqBy } from "lodash";
import { VoucherInterface } from "../types";
// @ts-ignore
import multiDownload from "multi-download";
import { SnackbarType } from "@workspace/shared";
import { AxiosResponse } from "axios";

const deleteVoucher = ({
  voucherIds,
}: {
  voucherIds: number[];
  status?: string;
}) => deleteVoucherById(voucherIds);
const downloadVoucher = (ids: number[]) => downloadVoucherById(ids);

type DeleteVoucherType = {
  ids: number[];
  callback?: Function;
  variables: { voucherIds: number[]; status: string };
  resetSelectedRows?(value: number[] | Function): void;
  setSnackbar: Function;
};

const onError = (message: string) => {
  // eslint-disable-next-line
  const setSnackbar = useSetRecoilState(snackbarAtom);
  setSnackbar((currentAlerts) => {
    const errorAlert: SnackbarType = {
      open: true,
      type: "error",
      message,
    }

    return [...currentAlerts, errorAlert]
  });
};

const updateQueryClientOnDelete = (
  key: Array<string | number>,
  voucherIds: number[]
) => {
  queryClient.setQueryData(key, (vouchers: any) => {
    if (vouchers?.data?.length) {
      return {
        ...vouchers,
        data: vouchers.data.filter(
          ({ id }: { id: number }) => !voucherIds.includes(id)
        ),
      };
    }
  });
};

const onDeleteVoucherSuccess = ({
  ids,
  variables,
  resetSelectedRows,
  callback,
  setSnackbar,
}: DeleteVoucherType) => {
  const { voucherIds, status } = variables;
  let key: Array<string | number> = ["vouchers"];

  if (ids?.length) {
    key = [...key, ...ids];
  }
  if (status) key.push(status);

  updateQueryClientOnDelete(key, voucherIds);

  if (resetSelectedRows) {
    resetSelectedRows((selectedRows: number[]) => {
      if (voucherIds.length === 1) {
        return selectedRows.filter((id: number) => id !== voucherIds[0]);
      } else return [];
    });
  }

  if (callback) callback();
  setSnackbar([{
    open: true,
    type: "success",
    message: `Voucher${voucherIds.length > 1 ? "s" : ""} successfully deleted`,
  }]);
};

export const useVoucherDelete = ({
  resetSelectedRows,
  ids,
  callback,
}: {
  resetSelectedRows?(value: number[]): void;
  ids: number[];
  callback?: Function;
}) => {
  const setSnackbar = useSetRecoilState(snackbarAtom);
  return useMutation(deleteVoucher, {
    onSuccess: (data: any, variables: any) =>
      onDeleteVoucherSuccess({
        ids,
        variables,
        resetSelectedRows,
        callback,
        setSnackbar,
      }),
    onError: () => onError(`Voucher delete failed`),
  });
};

const updateQueryClientVoucher = (data: any[], id: number) => {
  queryClient.setQueryData(["vouchers", id], (vouchers: any) => {
    if (!vouchers) {
      return {
        data: [...data],
        meta: {
          count: data.length,
        },
      };
    }
    return {
      data: uniqBy([...data, ...vouchers.data], "id"),
      meta: {
        count: vouchers.meta.count + data.length,
      },
    };
  });
};

type FileResponseIndexTuple = [PromiseSettledResult<AxiosResponse<any, any>>, number];

const onUploadFilesSuccess = ({
  id,
  data,
  files,
  setSnackbar,
  resetFiles,
}: {
  id: any,
  data: PromiseSettledResult<AxiosResponse<any, any>>[],
  files: FileList,
  setSnackbar: SetterOrUpdater<SnackbarType[]>,
  resetFiles: () => void
}) => {
  const successfulUploads = data.filter((req) => req.status === "fulfilled");

  const failedUploads = data
    .map((data, index) => [data, index] as FileResponseIndexTuple)
    .filter(([req, _]) => req.status === "rejected");

  const failedMessages = failedUploads.map(([req, fileIndex]) => {
    if (req.status === "fulfilled") return null;

    const fileName = files[fileIndex].name;
    const serverMessage = req.reason.response?.data?.error?.message;

    if (typeof serverMessage === "string" && serverMessage.length !== 0) {
      return `${serverMessage.trim()} File: ${fileName}`
    }

    return null;
  });

  resetFiles();
  queryClient.removeQueries("files");

  data = successfulUploads.map(
    ({
      value: {
        data: { data },
      },
    }: any) => data
  );

  updateQueryClientVoucher(data, id);

  setSnackbar((current) => {
    const failedUploadAlerts: SnackbarType[] = failedMessages.map(message => ({
      open: true,
      type: "error",
      message
    } as SnackbarType));

    const newAlerts: SnackbarType[] = [...failedUploadAlerts];

    if (successfulUploads.length > 0) {
      const totalUploads = successfulUploads.length + failedUploads.length;
      const totalUploadsText = failedUploads.length !== 0
        ? `${successfulUploads.length} out of ${totalUploads}`
        : `${successfulUploads.length}`;

      const usePlural = successfulUploads.length > 1 || failedUploads.length > 0;

      const successfullUploadsAlert: SnackbarType = {
        open: true,
        type: "success",
        message: `Successfully uploaded ${totalUploadsText} voucher${usePlural ? "s" : ""}.`,
      };

      newAlerts.unshift(successfullUploadsAlert);
    }

    return [...current, ...newAlerts];
  });

  resetFiles();

  queryClient.removeQueries("files");
};

export const useUploadFiles = (
  id: number | undefined,
  files: any,
  resetFiles: () => void
) => {
  const setSnackbar = useSetRecoilState(snackbarAtom);
  return useQuery(["files"], () => uploadVouchers(id, files), {
    enabled: !!(files && id),
    cacheTime: 0,
    onSuccess: data =>
      onUploadFilesSuccess({ id, data, files, setSnackbar, resetFiles }),
    onError: () => onError(`Voucher upload failed`),
  });
};

const getVouchersFn = async (
  id: number[],
  key: any[],
  optionalParams?: { status?: string; query?: string; count?: number }
) => {
  try {
    const data: any = await getVouchers({
      organizationId: id,
      queryString: `${optionalParams?.query ? `&${optionalParams.query}` : ""}${
        optionalParams?.status ? `&status=${optionalParams.status}` : ""
      }`,
      count: optionalParams?.count,
    });
    const existingData: any = queryClient.getQueryData(key);
    if (!existingData) return data;
    return {
      ...data,
      data: uniqBy([...data.data, ...existingData.data], "id"),
    };
  } catch (err: any) {
    throw err;
  }
};

const getKey = (
  id: number[],
  optionalParams:
    | { excludeOptionalKey?: boolean; status?: string; query?: string }
    | undefined
): Array<string & number> => {
  let key: any = ["vouchers"];

  if (id?.length) {
    key = [...key, ...id];
  }
  if (
    (optionalParams?.status || optionalParams?.query) &&
    !optionalParams.excludeOptionalKey
  ) {
    let additionalKey = optionalParams?.status || optionalParams?.query;
    key.push(additionalKey as string);
  }
  return key;
};

export const useGetVouchers = (
  id: number[],
  optionalParams?: {
    excludeOptionalKey?: boolean;
    status?: string;
    query?: string;
    count?: number;
  },
  options?: { enabled?: boolean; refetchInterval?: number | false }
) => {
  const key = getKey(id, optionalParams);
  return useQuery(
    key,
    async () => await getVouchersFn(id, key, optionalParams),
    {
      enabled: !!id && options?.enabled,
      refetchInterval:
        options?.refetchInterval !== undefined
          ? options.refetchInterval
          : 10000,
    }
  );
};

const onBulkVoucherDownloadSuccess = async (response: any, IDs: number[]) => {
  const files: any = [];
  queryClient.removeQueries("download-vouchers");
  response.forEach(({ value: { data } }: any, index: number) => {
    const newBlob = new Blob([data], { type: "application/pdf" });
    const blobData = window.URL.createObjectURL(newBlob);
    files.push(blobData);
  });
  multiDownload(files, {
    rename: ({ index }: any) => {
      return `voucher-${IDs[index]}.pdf`;
    },
  });
};

export const useBulkVouchersDownload = (IDs: number[]) => {
  return useQuery(["download-vouchers"], () => downloadVoucherById(IDs), {
    enabled: false,
    onSuccess: (response: any) => onBulkVoucherDownloadSuccess(response, IDs),
    onError: () => onError(`Voucher download failed`),
  });
};

const onSuccessSingleVoucherDownload = (response: any, variables: number[]) => {
  response.forEach(({ value: { data } }: any, index: number) => {
    const newBlob = new Blob([data], { type: "application/pdf" });
    const blobData = window.URL.createObjectURL(newBlob);
    let link = document.createElement("a");
    link.href = blobData;
    link.download = `voucher-${variables[index]}.pdf`;
    link.click();
  });
};

export const useSingleVoucherDownload = () => {
  return useMutation(downloadVoucher, {
    onSuccess: onSuccessSingleVoucherDownload,
    onError: () => onError(`Voucher download failed`),
  });
};

type vouchersByQueryArguments = {
  organizationId: number[];
  query: string;
  status?: string;
  count?: number;
  clearCache?: boolean;
};
const getVouchersByQuery = ({
  organizationId = [],
  query,
  count,
}: vouchersByQueryArguments): Promise<unknown[]> => {
  return getVouchers({ organizationId, queryString: query, count });
};

const updateQueryClientOnVouchersMutation = ({
  key,
  clearCache,
  data,
}: {
  key: Array<number | string>;
  clearCache?: boolean;
  data: { data: VoucherInterface[]; meta: { count: number } };
}) => {
  queryClient.setQueryData(key, (vouchers: any = { data: [] }) => {
    const dataList = clearCache ? data.data : [...vouchers.data, ...data.data];
    const count =
      data?.meta?.count !== undefined && data?.data?.length
        ? data?.meta?.count
        : vouchers?.meta?.count;

    return {
      data: uniqBy(dataList, "id"),
      meta: {
        count,
      },
    };
  });
};

const onSuccessLazyVouchersMutation = (
  data: { data: VoucherInterface[]; meta: { count: number } },
  {
    organizationId,
    status,
    clearCache,
  }: {
    organizationId: number[];
    query: string;
    status?: string;
    clearCache?: boolean;
  }
) => {
  let key: Array<number | string> = ["vouchers"];

  if (organizationId) {
    key = [...key, ...organizationId];
  }
  if (status) key.push(status);
  updateQueryClientOnVouchersMutation({ key, clearCache, data });
};

export const useLazyVouchersMutation = () => {
  // @ts-ignore
  return useMutation(getVouchersByQuery, {
    onSuccess: onSuccessLazyVouchersMutation,
  });
};

export const useGetVoucherHistory = (id: number) => {
  return useQuery(
    `voucher-history-${id}`,
    async () => await getVoucherHistory(id),
    {
      enabled: !!id,
      refetchInterval: undefined,
      cacheTime: 1000 * 60 * 5,
    }
  );
};
